import {
  ITripCreateRequest,
  ITripRequest,
  ITripStructureRequest,
} from "../../../models/trip/request";

const STORAGE_KEY = "trip:create-attempt:v2";
const TRIP_CREATE_ENDPOINT = "/trips";
const TRIP_CREATE_ACTION = "create";
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type TripCreateIntent = "create" | "create-and-start";

interface IStoredTripCreateIdentity {
  version: 2;
  endpoint: typeof TRIP_CREATE_ENDPOINT;
  action: typeof TRIP_CREATE_ACTION;
  ownerKey: string;
  intent: TripCreateIntent;
  driverId: number;
  orderIds: number[];
  fingerprint: string;
  createToken: string;
}

export interface ITripCreateAttempt {
  ownerKey: string;
  intent: TripCreateIntent;
  fingerprint: string;
  request: ITripCreateRequest;
}

let memoryIdentity: IStoredTripCreateIdentity | null = null;

const createUuidV4 = (): string => {
  const cryptoApi = globalThis.crypto;
  const randomUuid = cryptoApi as Crypto & {
    randomUUID?: () => string;
  };

  if (typeof randomUuid?.randomUUID === "function") {
    return randomUuid.randomUUID.call(cryptoApi);
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("Secure UUID generation is unavailable");
  }
  cryptoApi.getRandomValues(bytes);

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

const readStoredIdentity = (
  ownerKey: string,
): IStoredTripCreateIdentity | null => {
  const memoryForOwner =
    memoryIdentity?.ownerKey === ownerKey ? memoryIdentity : null;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return memoryForOwner;

    const parsed = JSON.parse(raw) as Partial<IStoredTripCreateIdentity>;
    if (
      parsed.version !== 2 ||
      parsed.endpoint !== TRIP_CREATE_ENDPOINT ||
      parsed.action !== TRIP_CREATE_ACTION ||
      typeof parsed.ownerKey !== "string" ||
      (parsed.intent !== "create" && parsed.intent !== "create-and-start") ||
      typeof parsed.driverId !== "number" ||
      !Array.isArray(parsed.orderIds) ||
      !parsed.orderIds.every((orderId) => typeof orderId === "number") ||
      typeof parsed.fingerprint !== "string" ||
      typeof parsed.createToken !== "string" ||
      !UUID_V4_PATTERN.test(parsed.createToken)
    ) {
      return memoryForOwner;
    }

    if (parsed.ownerKey !== ownerKey) {
      memoryIdentity = null;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // The stale identity remains inaccessible because its owner differs.
      }
      return null;
    }

    memoryIdentity = parsed as IStoredTripCreateIdentity;
    return memoryIdentity;
  } catch {
    return memoryForOwner;
  }
};

const storeIdentity = (identity: IStoredTripCreateIdentity): void => {
  memoryIdentity = identity;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // The in-memory fallback still protects retries during this page lifetime.
  }
};

export const dedupeTripOrderIds = (orderIds?: number[]): number[] =>
  Array.from(new Set(orderIds ?? []));

export const buildTripStructureRequest = (
  data: ITripRequest,
): ITripStructureRequest => ({
  driverId: Number(data.driverId),
  orderIds: dedupeTripOrderIds(data.orderIds),
});

export const getOrCreateTripCreateAttempt = (
  data: ITripRequest,
  ownerKey: string,
  intent: TripCreateIntent,
): ITripCreateAttempt => {
  const structure = buildTripStructureRequest(data);
  const fingerprint = JSON.stringify({
    endpoint: TRIP_CREATE_ENDPOINT,
    action: TRIP_CREATE_ACTION,
    ownerKey,
    driverId: structure.driverId,
    orderIds: [...structure.orderIds].sort(
      (firstOrderId, secondOrderId) => firstOrderId - secondOrderId,
    ),
  });
  const stored = readStoredIdentity(ownerKey);
  const createToken =
    stored?.fingerprint === fingerprint ? stored.createToken : createUuidV4();

  storeIdentity({
    version: 2,
    endpoint: TRIP_CREATE_ENDPOINT,
    action: TRIP_CREATE_ACTION,
    driverId: structure.driverId,
    ownerKey,
    intent,
    orderIds: structure.orderIds,
    fingerprint,
    createToken,
  });

  return {
    ownerKey,
    intent,
    fingerprint,
    request: {
      ...structure,
      createToken,
    },
  };
};

export const getPendingTripCreateAttempt = (
  ownerKey: string,
): ITripCreateAttempt | null => {
  const stored = readStoredIdentity(ownerKey);
  if (!stored) return null;

  return {
    ownerKey: stored.ownerKey,
    intent: stored.intent,
    fingerprint: stored.fingerprint,
    request: {
      driverId: stored.driverId,
      orderIds: [...stored.orderIds],
      createToken: stored.createToken,
    },
  };
};
export const clearTripCreateAttempt = (attempt: ITripCreateAttempt): void => {
  const stored = readStoredIdentity(attempt.ownerKey);
  if (
    stored?.fingerprint !== attempt.fingerprint ||
    stored.createToken !== attempt.request.createToken
  ) {
    return;
  }

  memoryIdentity = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Memory was already cleared.
  }
};
